"use client";

/**
 * Site-wide controls. The current snapshot is fetched server-side in the
 * root layout and passed in as a prop, so this component does no client
 * fetching, no setState, and no polling — that's deliberate. Doing
 * setState at the layout level on every render caused duplicate testid
 * trees in Playwright (Next.js streaming renders children twice when the
 * top-level wrapper transitions state during hydration).
 *
 * Trade-off: when an admin flips a toggle, active visitors see the change
 * on their next navigation / reload rather than instantly. That's
 * acceptable for the operator-grade controls (banner / takeover / payments).
 *
 * Children are wrapped with a SiteStatusContext.Provider so consumers
 * (e.g. Checkout) can read the snapshot via useSiteStatus().
 */

import { createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "@/lib/auth-react";
import { CookieConsent } from "@/components/CookieConsent";

type Level = "info" | "warning" | "danger";
type Status = "NORMAL" | "MAINTENANCE" | "EMERGENCY";

export type PaymentProvider = "payfort" | "paypal" | "stripe";

export interface SiteStatusSnapshot {
  status: Status;
  statusMessage: string;
  banner: { enabled: boolean; message: string; level: Level };
  payments: { enabled: boolean; disabledMessage: string; provider: PaymentProvider };
  cookieBanner: { enabled: boolean; message: string };
  /** Session-replay capture kill-switch. Default OFF; consumed by ReplaySession. */
  replay: { enabled: boolean };
}

const DEFAULT: SiteStatusSnapshot = {
  status: "NORMAL",
  statusMessage: "",
  banner: { enabled: false, message: "", level: "info" },
  payments: { enabled: false, disabledMessage: "", provider: "payfort" },
  cookieBanner: { enabled: false, message: "" },
  replay: { enabled: false },
};

const POLL_MS = 30_000;

/** Tailwind background per banner level. Exported so admin previews
 * (in PlatformSettingsContent) render identical colours. */
export const BANNER_BG: Record<Level, string> = {
  info: "bg-sky-600",
  warning: "bg-amber-600",
  danger: "bg-red-600",
};

// Admins / auth flows keep working in any state; otherwise the takeover
// would lock the admin out of fixing it.
const TAKEOVER_BYPASS_PREFIXES = ["/auth", "/login", "/register", "/api", "/dashboard"];

const SiteStatusContext = createContext<SiteStatusSnapshot>(DEFAULT);

export function useSiteStatus(): SiteStatusSnapshot {
  return useContext(SiteStatusContext);
}

export function SiteStatusGate({
  children,
  snapshot,
}: {
  children: React.ReactNode;
  snapshot: SiteStatusSnapshot;
}) {
  return (
    <SiteStatusContext.Provider value={snapshot}>
      {/* Banner is `sticky top-0` so it stays pinned at the very top and
          never scrolls away. It publishes its height as --app-banner-h; the
          sticky header pins just below it (`top: --app-banner-h`) and the
          viewport-fixed dashboard sidebar subtracts the same var from its
          height. Keeping the banner pinned makes all three offsets constant,
          so there's no header/sidebar gap on scroll. Takeover stays a fixed
          portal overlay. */}
      <SiteBanner snapshot={snapshot} />
      {children}
      <CookieConsent banner={snapshot.cookieBanner} />
      <SiteTakeoverOverlay snapshot={snapshot} />
    </SiteStatusContext.Provider>
  );
}

/**
 * Site-wide announcement banner. `sticky top-0` so it stays pinned at the
 * top (see the SiteStatusGate note). Reads from context-derived props — no
 * fetching. Gated on `mounted` to keep SSR/CSR output identical (pathname
 * + session resolve client-side).
 */
function SiteBanner({ snapshot }: { snapshot: SiteStatusSnapshot }) {
  const pathname = usePathname();
  const { data: session } = useSession();
  const [mounted, setMounted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => setMounted(true), []);

  const isAdmin = session?.user?.role === "ADMIN";
  const onBypassRoute = TAKEOVER_BYPASS_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
  // Hidden in the live meeting room — the call screen shows its own session
  // countdown, so a site-wide banner there only steals the vertical space the
  // video UI needs. Also redundant while a takeover overlay is showing.
  const isMeetingRoute =
    pathname === "/meeting" || pathname.startsWith("/meeting/");
  const shouldShow =
    mounted &&
    snapshot.banner.enabled &&
    Boolean(snapshot.banner.message) &&
    !isMeetingRoute &&
    !(snapshot.status !== "NORMAL" && !isAdmin && !onBypassRoute);

  // Publish the banner's live height as --app-banner-h so viewport-fixed
  // chrome (the dashboard sidebar) can subtract it. Without this, the fixed
  // sidebar is pinned to the viewport top and its first item slides behind
  // the sticky header once the in-flow banner pushes the header down.
  useEffect(() => {
    const root = document.documentElement;
    const el = ref.current;
    if (!shouldShow || !el) {
      root.style.setProperty("--app-banner-h", "0px");
      return;
    }
    const update = () =>
      root.style.setProperty("--app-banner-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--app-banner-h", "0px");
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  return (
    <div
      ref={ref}
      data-testid="siteBanner"
      data-level={snapshot.banner.level}
      className={`sticky top-0 z-40 ${BANNER_BG[snapshot.banner.level]} px-4 py-2 text-center text-sm font-medium text-white`}
    >
      {snapshot.banner.message}
    </div>
  );
}

/**
 * Maintenance / emergency takeover — a fixed full-screen portal overlay.
 * Reads from context-derived props only — no state beyond the mount gate
 * that `createPortal` requires.
 */
function SiteTakeoverOverlay({ snapshot }: { snapshot: SiteStatusSnapshot }) {
  const pathname = usePathname();
  const { data: session } = useSession();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  const isAdmin = session?.user?.role === "ADMIN";
  const onBypassRoute = TAKEOVER_BYPASS_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );
  const showTakeover =
    snapshot.status !== "NORMAL" && !isAdmin && !onBypassRoute;

  if (!showTakeover) return null;
  return createPortal(<Takeover snapshot={snapshot} />, document.body);
}

function Takeover({ snapshot }: { snapshot: SiteStatusSnapshot }) {
  const isEmergency = snapshot.status === "EMERGENCY";
  const heading = isEmergency ? "We're temporarily offline" : "Scheduled maintenance";
  return (
    <div
      data-testid="siteTakeover"
      data-status={snapshot.status}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-black px-6 text-center text-white"
    >
      <h1 className="mb-4 text-3xl font-semibold">{heading}</h1>
      <p className="max-w-xl whitespace-pre-line text-base leading-relaxed text-white/80">
        {snapshot.statusMessage ||
          (isEmergency
            ? "Rawiah is unavailable right now. We're working on it and will be back as soon as possible."
            : "Rawiah is undergoing maintenance. Please check back shortly.")}
      </p>
    </div>
  );
}
